import React, {Fragment} from 'react';
import PropTypes from 'prop-types';

// Relative Imports
import DestinationCarouselPlaceholder from './DestinationCarouselPlaceholder';
import DestinationCarousel from './DestinationCarousel';
import {
    DEFAULT_BREAKPOINTS,
    DESTINATION_WEB_TARGETS,
} from '../../utils/constants';

// eslint-disable-next-line complexity
function DestinationPropertiesCarousel(props) {
    const {
        breakpoints,
        cardSizeSm,
        isLoading,
        placeholdersQuantity,
        scrolling,
    } = props;

    return (
        <Fragment>
            {isLoading ? (
                <DestinationCarouselPlaceholder
                    breakpoints={breakpoints}
                    cardSizeSm={cardSizeSm}
                    placeholdersQuantity={placeholdersQuantity}
                    scrolling={scrolling}
                />
            ) : (
                <DestinationCarousel {...props} />
            )}
        </Fragment>
    );
}

export default DestinationPropertiesCarousel;

DestinationPropertiesCarousel.propTypes = {
    /** Aspect ratio of card images. Refer to @homeaway/react-aspect-ratio-card to see available options */
    aspectRatio: PropTypes.string,
    /** Specifies the viewport width breakpoints and how many items to display in the carousel at each breakpoint. */
    breakpoints: PropTypes.objectOf(PropTypes.number),
    /** Target attribute for card link (i.e. "_blank" to have clicks open in new tab) */
    cardLinkTarget: PropTypes.string,
    /** Additional class names for style tuning */
    className: PropTypes.string,
    /** Adjusts styles making the carousel a bit more compact */
    cardSizeSm: PropTypes.bool,
    /** Use @homeaway/graphql-traveler-recommendation partial to extract the value for this field */
    clientRequestId: PropTypes.string.isRequired,
    /** Use this prop to pass additional handlers to card click event */
    customCardClickHandler: PropTypes.func,
    /** Specifies whether the carousel is in loading state (waiting for a response from graphql or service) */
    isLoading: PropTypes.bool,
    /** Specifies whether the carousel is being used on a mobile device */
    isMobile: PropTypes.bool,
    /** Allows to change default layout of the carousel. Refer to @homeaway/react-card-carousel for more details */
    layoutAlignment: PropTypes.oneOf([
        'container',
        'cards',
        'cardsWithOverlapButtons',
        'buttons',
    ]),
    /** A function to be called when cards inside the carousel change */
    onCardsShownChange: PropTypes.func,
    /** How many placeholder cards to display while the data is fetching */
    placeholdersQuantity: PropTypes.number,
    /** An array of recommended listings to display in a form of a card. Use @homeaway/graphql-traveler-recommendation partial to get the correct value for this prop */
    data: PropTypes.arrayOf(
        PropTypes.shape({
            destinationUrl: PropTypes.string,
            imageHref: PropTypes.string,
            searchTermUuid: PropTypes.string,
            place: PropTypes.shape({
                name: PropTypes.shape({
                    simple: PropTypes.string,
                    full: PropTypes.string,
                }),
            }),
        })
    ).isRequired,
    /** Specifies whether the carousel should be displayed in the regular or scrolling state */
    scrolling: PropTypes.bool,
    /** Offset margin for scrolling from edge to edge of viewport */
    scrollMarginOffset: PropTypes.number,
    /** Subheading for the carousel */
    subheading: PropTypes.string,
    /** Client app's specific identifier */
    target: PropTypes.oneOf(Object.values(DESTINATION_WEB_TARGETS)).isRequired,
    /** Main title for the carousel */
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

DestinationPropertiesCarousel.defaultProps = {
    aspectRatio: '3x2',
    breakpoints: DEFAULT_BREAKPOINTS.CLICKING,
    className: '',
    clientRequestId: '',
    cardSizeSm: false,
    customCardClickHandler: () => {},
    isLoading: true,
    isMobile: false,
    isPinnedPropertySERP: false,
    layoutAlignment: 'container',
    scrolling: false,
    scrollMarginOffset: 0,
    subheading: '',
    title: '',
    data: [],
};
